/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\AuthCodesTab.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the PBX Incoming Call Tools module.
----------------------------------------------------------------*/


import { Key, Plus, Trash2, Upload } from 'lucide-react';
import { FormBox } from '../../../components/ui/FormBox';
import { Label } from '../../../components/ui/label';
import { Input } from '../../../components/ui/input';
import Toggle from '../../../components/ui/Toggle';
import SimpleTooltip from '../../../components/ui/SimpleTooltip';
import type { AuthenticationCode } from '../types';

interface AuthCodesTabProps {
  codes: AuthenticationCode[];
  isEditMode: boolean;
  onAddCode: () => void;
  onDeleteCode: (index: number) => void;
  onCodeChange: (index: number, field: keyof AuthenticationCode, value: unknown) => void;
  onImport: () => void;
  t: (key: string, fallback?: string) => string;
}

export function AuthCodesTab({
  codes, isEditMode, onAddCode, onDeleteCode, onCodeChange, onImport, t,
}: AuthCodesTabProps) {
  return (
    <FormBox
      title={t('authenticationCodes.tabs.codes')}
      icon={<Key className="h-5 w-5" />}
      actions={
        isEditMode
          ? [{
            label: t('authenticationCodes.actions.importCodes'),
            onClick: onImport,
            icon: <Upload className="h-4 w-4" />,
          }]
          : undefined
      }
    >
      <div className="space-y-4">
        {codes.length === 0 ? (
          <div className="py-8 text-center text-muted-foreground">
            {t('authenticationCodes.empty.codes')}
          </div>
        ) : (
          <div className="space-y-3">
            {/* Header row */}
            <div className="flex items-center gap-4 border-b border-border pb-2 text-xs font-medium uppercase text-muted-foreground">
              <div className="flex-1 min-w-0">{t('authenticationCodes.fields.code.label')}</div>
              <div className="flex-1 min-w-0">{t('authenticationCodes.fields.codeLabel.label')}</div>
              <div className="w-32 text-center">{t('authenticationCodes.fields.enabled.label')}</div>
              <div className="w-20" />
            </div>

            {codes.map((code, index) => (
              <div key={`${code.id ?? 'new'}-${index}`} className="rounded-lg border border-border p-3">
                <div className="flex items-center gap-4">
                  {/* Code input */}
                  <div className="flex-1 min-w-0">
                    <Label className="mb-1 block text-xs text-muted-foreground" htmlFor={`code-${index}`}>
                      {t('authenticationCodes.fields.code.label')}
                      <span className="ml-1 text-red-500">*</span>
                    </Label>
                    <Input
                      id={`code-${index}`}
                      value={code.code}
                      onChange={(e) => onCodeChange(index, 'code', e.target.value)}
                      placeholder={t('authenticationCodes.fields.code.placeholder')}
                      className="h-9"
                    />
                  </div>

                  {/* Label input */}
                  <div className="flex-1 min-w-0">
                    <Label className="mb-1 block text-xs text-muted-foreground" htmlFor={`label-${index}`}>
                      {t('authenticationCodes.fields.codeLabel.label')}
                    </Label>
                    <Input
                      id={`label-${index}`}
                      value={code.label || ''}
                      onChange={(e) => onCodeChange(index, 'label', e.target.value)}
                      placeholder={t('authenticationCodes.fields.codeLabel.placeholder')}
                      className="h-9"
                    />
                  </div>

                  {/* Enabled toggle */}
                  <div className="flex w-32 items-center justify-center">
                    <Toggle value={code.enabled} onChange={(v) => onCodeChange(index, 'enabled', v)} />
                  </div>

                  {/* Delete button */}
                  <div className="flex w-20 justify-end">
                    <SimpleTooltip content={t('common:delete')} position="top">
                      <button
                        type="button"
                        onClick={() => onDeleteCode(index)}
                        aria-label={t('common:delete')}
                        className="rounded-md p-2 text-red-600 transition-colors hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </SimpleTooltip>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="flex justify-end border-t border-border pt-4">
          <button
            type="button"
            onClick={onAddCode}
            className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium text-primary transition-colors hover:bg-primary/10 hover:text-primary/90"
          >
            <Plus className="h-4 w-4" />
            {t('common:add')}
          </button>
        </div>
      </div>
    </FormBox>
  );
}
